import React from "react";
import { Box, Text, Title, Code, Table, Badge, Image } from "@mantine/core";
import config from "../../config";

function inline(text, keyPrefix = "i") {
	const parts = String(text || "").split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g);
	return parts.map((part, index) => {
		if (part.startsWith("`") && part.endsWith("`")) return <Code key={`${keyPrefix}-${index}`}>{part.slice(1, -1)}</Code>;
		if (part.startsWith("**") && part.endsWith("**")) return <strong key={`${keyPrefix}-${index}`}>{part.slice(2, -2)}</strong>;
		if (part.startsWith("*") && part.endsWith("*")) return <em key={`${keyPrefix}-${index}`}>{part.slice(1, -1)}</em>;
		return <React.Fragment key={`${keyPrefix}-${index}`}>{part}</React.Fragment>;
	});
}

function parseTable(lines, startIndex) {
	const rows = [];
	let i = startIndex;
	while (i < lines.length && /^\|.*\|$/.test(lines[i])) {
		const cells = lines[i].split('|').slice(1, -1).map(c => c.trim());
		rows.push(cells);
		i++;
	}
	return { rows, endIndex: i };
}

function parseList(lines, startIndex) {
	const items = [];
	let i = startIndex;
	while (i < lines.length && lines[i].match(/^[-*]\s+/)) {
		items.push(lines[i].replace(/^[-*]\s+/, ''));
		i++;
	}
	return { items, endIndex: i };
}

function parseCodeBlock(lines, startIndex) {
	const codeLines = [];
	let i = startIndex + 1;
	while (i < lines.length && !lines[i].startsWith('```')) {
		codeLines.push(lines[i]);
		i++;
	}
	return { code: codeLines.join('\n'), language: lines[startIndex].slice(3), endIndex: i + 1 };
}

function imageSrc(src = "") {
	const clean = String(src || "").trim();
	if (!clean) return "";
	if (/^(https?:)?\/\//i.test(clean) || clean.startsWith("data:")) return clean;
	if (clean.startsWith("/")) return `${config.api.API_URL}${clean}`;
	if (clean.startsWith("./screenshots/") || clean.startsWith("screenshots/")) {
		return `${config.api.API_URL}/generated-docs/${clean.replace(/^\.\//, "")}`;
	}
	return clean;
}

export default function MarkdownPreview({ markdown = "" }) {
	const lines = String(markdown || "").split("\n");
	const elements = [];
	let i = 0;

	while (i < lines.length) {
		const line = lines[i];

		if (line.startsWith("# ")) {
			elements.push(<Title key={`h1-${i}`} order={2} mt="lg" mb="md" fw={700}>{inline(line.slice(2), `h1-${i}`)}</Title>);
			i++;
		} else if (line.startsWith("## ")) {
			elements.push(<Title key={`h2-${i}`} order={3} mt="lg" mb="sm" fw={600}>{inline(line.slice(3), `h2-${i}`)}</Title>);
			i++;
		} else if (line.startsWith("### ")) {
			elements.push(<Title key={`h3-${i}`} order={4} mt="md" mb="xs" fw={500}>{inline(line.slice(4), `h3-${i}`)}</Title>);
			i++;
		} else if (line.startsWith('```')) {
			const { code, language, endIndex } = parseCodeBlock(lines, i);
			elements.push(
				<Box key={`code-${i}`} my="md">
					<Code block p="md">
						{language ? <Badge size="sm" mb="sm">{language}</Badge> : null}
						<Text component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{code}</Text>
					</Code>
				</Box>,
			);
			i = endIndex;
		} else if (/^!\[[^\]]*]\([^)]+\)\s*$/.test(line.trim())) {
			const match = line.trim().match(/^!\[([^\]]*)]\(([^)]+)\)\s*$/);
			const alt = match?.[1] || "Screenshot";
			const src = imageSrc(match?.[2] || "");
			elements.push(
				<Box key={`image-${i}`} my="md">
					<Image
						src={src}
						alt={alt}
						radius="sm"
						withPlaceholder
						sx={{
							border: '1px solid var(--mantine-color-gray-3)',
							overflow: 'hidden',
						}}
					/>
				</Box>,
			);
			i++;
		} else if (/^\|.*\|$/.test(line)) {
			const { rows, endIndex } = parseTable(lines, i);
			if (rows.length > 0) {
				const headers = rows[0];
				const body = rows.slice(1).filter(row => !row.every(c => c === '-'));
				elements.push(
					<Box key={`table-${i}`} my="md" sx={{ overflowX: 'auto' }}>
						<Table striped highlightOnHover sx={{ minWidth: 600 }}>
							<thead>
								<tr>
									{headers.map((h, idx) => <th key={idx}>{h}</th>)}
								</tr>
							</thead>
							<tbody>
								{body.map((row, ridx) => (
									<tr key={ridx}>
										{row.map((cell, cidx) => <td key={cidx}>{cell}</td>)}
									</tr>
								))}
							</tbody>
						</Table>
					</Box>,
				);
			}
			i = endIndex;
		} else if (line.match(/^[-*]\s+/)) {
			const listIndex = i;
			const { items, endIndex } = parseList(lines, i);
			elements.push(
				<Box key={`list-${listIndex}`} component="ul" sx={{ paddingLeft: 24, marginTop: 12, marginBottom: 12 }}>
					{items.map((item, idx) => (
						<Text key={idx} component="li" sx={{ marginBottom: 8 }}>
							{inline(item, `li-${listIndex}-${idx}`)}
						</Text>
					))}
				</Box>,
			);
			i = endIndex;
		} else if (line.startsWith("> ")) {
			elements.push(
				<Box
					key={`blockquote-${i}`}
					sx={{
						borderLeft: '4px solid var(--mantine-color-blue-6)',
						paddingLeft: 16,
						marginLeft: 0,
						marginY: 12,
						fontStyle: 'italic',
						color: 'var(--mantine-color-gray-7)',
					}}
				>
					{inline(line.slice(2), `blockquote-${i}`)}
				</Box>,
			);
			i++;
		} else if (!line.trim()) {
			elements.push(<Box key={`spacer-${i}`} h={6} />);
			i++;
		} else {
			elements.push(
				<Text key={`p-${i}`} sx={{ marginBottom: 12, lineHeight: 1.7 }}>
					{inline(line, `p-${i}`)}
				</Text>,
			);
			i++;
		}
	}

	return (
		<Box sx={{ lineHeight: 1.7, '& code': { backgroundColor: 'var(--mantine-color-gray-1)', padding: '2px 6px', borderRadius: 4 } }}>
			{elements}
		</Box>
	);
}
